import { Card, mergeClasses, PortalProps, Toaster } from "@fluentui/react-components";
import { DragEvent, DragEventHandler, memo, useState } from "react";
import { DEFAULT_APPLICATION_ID } from "../../constants";
import { useAppData, useToastNotification } from "../../hooks";
import { IClause } from "../../interfaces";
import { ClauseCardProps } from "../../types/ClauseCardProps";
import { ClauseCardMode } from "../../types/enums";
import { ClauseCardSideAction } from "./components/ClauseCardSideAction";
import { ClauseFootnote } from "./components/ClauseFootnote";
import { ClauseTextRender } from "./components/ClauseTextRender";
import { useClauseCardStyles } from "./styles/ClauseCard.styles";

export const ClauseCard = memo(({ showScroll = false, ...props }: ClauseCardProps & Pick<PortalProps, "mountNode">) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [hasTextOverflow, setHasTextOverflow] = useState<boolean>(false);
  const { toasterId } = useToastNotification();
  const styles = useClauseCardStyles();
  // Use per-app goodSuggestions when appId is provided, fallback to global/default app otherwise
  const currentAppId = props.appId || DEFAULT_APPLICATION_ID;
  const [goodSuggestions] = useAppData<Array<IClause>>(currentAppId, "goodSuggestions", []);
  const isLikedClause = goodSuggestions.some((clause) => clause.clause_id === props.clause.clause_id);

  let clauseTextRenderClassName = "";

  if (expanded) {
    clauseTextRenderClassName = showScroll ? styles.clauseTextExpanded : "";
  } else {
    clauseTextRenderClassName = styles.clauseTextReader;
  }

  const handleClauseDrag: DragEventHandler = (ev: DragEvent) => {
    ev.dataTransfer.setData("text/plain", props.clause.clause_text);
  };

  const handleDragStartWithSelectionCheck = (
    e: DragEvent<HTMLDivElement>,
    callback: (e: DragEvent<HTMLDivElement>) => void
  ) => {
    const selection = window.getSelection();
    if (!selection || selection.toString().length === 0) {
      callback(e);
    } else {
      e.preventDefault();
    }
  };
  return (
    <div className={styles.clauseCardContainer} style={{ marginBottom: "10px", marginRight: "15px" }}>
      <Toaster toasterId={toasterId} position={window.Office ? "bottom-start" : "bottom-end"}></Toaster>
      <Card
        className={mergeClasses(
          styles.clauseCard,
          isLikedClause ? styles.clauseAccepted : "",
          props.clause.is_endorsed ? styles.clauseEndorsed : ""
        )}
      >
        <div className={styles.clauseCardContentWrapper}>
          <ClauseCardSideAction
            {...props}
            expanded={expanded}
            setExpanded={setExpanded}
            hasTextOverflow={hasTextOverflow}
            setHasTextOverflow={setHasTextOverflow}
            workspaceId={props.workspaceId}
          />
          <div
            draggable
            onDragStart={(e) => handleDragStartWithSelectionCheck(e, handleClauseDrag)}
            className={mergeClasses(styles.clauseTextContainer)}
          >
            <ClauseTextRender
              clause={props.clause}
              searchText={props.searchText}
              primaryKeywords={props.searchKeywords}
              secondaryKeywords={props.filterKeywords}
              shouldRedline={props.readlineEnabled}
              highlightText={props.unusualPhrases?.highlightedText || ""}
              shouldHighlightKeywords={props.footNoteItems?.includes("bold_keywords")!}
              isKeywordSearch={props.isKeywordSearch}
              hasTextOverflow={(isTextOverflow) => setHasTextOverflow(isTextOverflow || expanded)}
              className={clauseTextRenderClassName}
            />
          </div>
        </div>
      </Card>
      {props.mode !== ClauseCardMode.DETAIL && (
        <div key={`footnote_container_${props.clause.clause_id}`}>
          <ClauseFootnote {...props} />
        </div>
      )}
    </div>
  );
});
